import React from 'react';
import styles from './Communities.module.css';
import { PrimaryButton, DefaultButton } from '@fluentui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { Notification } from '../Components/Notification';
import { CreateTenantModal } from '../Components/CreateTenantModal';
import { CreateCommunityModal } from '../Components/CreateCommunityModal';
import { CreatePortfolioModal } from '../Components/CreatePortfolioModal';
import { useConfirmAction } from '../hooks';
import { StandardTable } from '../Components/StandardTable';
import { TenantDirectoryTable } from '../Components/TenantDirectoryTable';
import { apiClient } from '../Services/api-client';

export const Communities: React.FC = () => {
  const { portfolioId } = useParams<{ portfolioId: string }>();
  const navigate = useNavigate();
  const tenantDataEvent = 'tenant-data-updated';
  const [portfolio, setPortfolio] = React.useState<any>(null);
  const [communities, setCommunities] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [isCreateOpen, setIsCreateOpen] = React.useState(false);
  const [isEditPortfolioOpen, setIsEditPortfolioOpen] = React.useState(false);
  const [editingCommunity, setEditingCommunity] = React.useState<any | null>(null);
  const [notification, setNotification] = React.useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [tenantLoading, setTenantLoading] = React.useState(false);
  const [tenantView, setTenantView] = React.useState<{
    title: string;
    tenants: Array<{ emailNorm: string; firstName?: string; lastName?: string }>;
    portfolioId?: string;
    communityId?: string;
  } | null>(null);
  const [tenantSearch, setTenantSearch] = React.useState('');
  const [isCreateTenantOpen, setIsCreateTenantOpen] = React.useState(false);
  const [createTenantDefaults, setCreateTenantDefaults] = React.useState<{
    firstName: string;
    lastName: string;
    email: string;
    portfolioId: string;
    communityId: string;
  }>({
    firstName: '',
    lastName: '',
    email: '',
    portfolioId: '',
    communityId: '',
  });
  const { openConfirm, dialog: confirmDialog } = useConfirmAction();
  const [tenantAccessMap, setTenantAccessMap] = React.useState<
    Record<string, Array<{ portfolioId: string; communityId: string }>>
  >({});
  const [portfolioNameMap, setPortfolioNameMap] = React.useState<Record<string, string>>({});
  const [communityNameMap, setCommunityNameMap] = React.useState<Record<string, string>>({});

  const capitalizeFirst = React.useCallback((value: string) => {
    if (!value) {
      return value;
    }
    return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
  }, []);

  const formatName = React.useCallback(
    (value?: string) => (value ? capitalizeFirst(value) : ''),
    [capitalizeFirst]
  );

  const handleCopy = React.useCallback(
    async (value: string, label = 'Value') => {
      try {
        await navigator.clipboard.writeText(value);
        setNotification({
          type: 'success',
          message: `${label} copied to clipboard`,
        });
      } catch (error) {
        setNotification({
          type: 'error',
          message: `Failed to copy ${label.toLowerCase()}`,
        });
      }
    },
    [setNotification]
  );

  const loadData = React.useCallback(async () => {
    if (!portfolioId) return;
    setLoading(true);
    try {
      const [portfolioRes, communitiesRes] = await Promise.all([
        apiClient.get(`/portfolios/${portfolioId}`),
        apiClient.get(`/portfolios/${portfolioId}/communities`),
      ]);
      setPortfolio(portfolioRes.data);
      setCommunities(communitiesRes.data || []);
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Failed to load data',
      });
    } finally {
      setLoading(false);
    }
  }, [portfolioId]);

  const loadCommunitiesForModal = React.useCallback(
    async (targetPortfolioId: string) => {
      if (!targetPortfolioId) {
        setCommunities([]);
        return;
      }
      try {
        const response = await apiClient.get(
          `/portfolios/${targetPortfolioId}/communities`
        );
        setCommunities(response.data || []);
      } catch (error) {
        console.error('Failed to load communities');
      }
    },
    []
  );

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEditCommunity = (community: any) => {
    setEditingCommunity(community);
    setIsCreateOpen(true);
  };

  const handleDeleteCommunity = async (communityId: string) => {
    if (!portfolioId) return;
    try {
      setLoading(true);
      await apiClient.delete(`/portfolios/${portfolioId}/communities/${communityId}`);
      setNotification({
        type: 'success',
        message: 'Community deleted successfully',
      });
      await loadData();
    } catch (error: any) {
      setNotification({
        type: 'error',
        message: error?.response?.data?.error || 'An error occurred',
      });
    } finally {
      setLoading(false);
    }
  };

  const openCreateTenantModal = (communityId?: string) => {
    if (!portfolioId) return;
    setCreateTenantDefaults({
      firstName: '',
      lastName: '',
      email: '',
      portfolioId,
      communityId: communityId || '',
    });
    setIsCreateTenantOpen(true);
  };

  const loadPortfolioTenants = async () => {
    if (!portfolioId) return;
    try {
      setTenantLoading(true);
      const response = await apiClient.get(
        `/tenant-access/portfolio/${portfolioId}/tenants`
      );
      const data = response.data;
      setTenantView({
        title: 'Tenants in Portfolio Communities',
        tenants: data || [],
        portfolioId,
      });
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Failed to load portfolio tenants',
      });
    } finally {
      setTenantLoading(false);
    }
  };

  const loadCommunityTenants = async (communityId: string) => {
    if (!portfolioId) return;
    try {
      setTenantLoading(true);
      const response = await apiClient.get(
        `/tenant-access/community/${communityId}/tenants`,
        { params: { portfolioId } }
      );
      const data = response.data;
      setTenantView({
        title: 'Tenants with Community Access',
        tenants: data || [],
        portfolioId,
        communityId,
      });
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Failed to load community tenants',
      });
    } finally {
      setTenantLoading(false);
    }
  };

  const refreshTenantView = React.useCallback(() => {
    if (!tenantView?.portfolioId) {
      return;
    }
    if (tenantView.communityId) {
      loadCommunityTenants(tenantView.communityId);
    } else {
      loadPortfolioTenants();
    }
  }, [tenantView, loadCommunityTenants, loadPortfolioTenants]);

  React.useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'tenantDataVersion') {
        refreshTenantView();
      }
    };

    const handleEvent = () => {
      refreshTenantView();
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener(tenantDataEvent, handleEvent);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(tenantDataEvent, handleEvent);
    };
  }, [refreshTenantView, tenantDataEvent]);

  React.useEffect(() => {
    const loadAccessNames = async () => {
      const accessEntries: Array<{ portfolioId: string; communityId: string }> = [];

      Object.values(tenantAccessMap).forEach((accesses) => {
        accessEntries.push(...accesses);
      });

      const portfolioIds = Array.from(
        new Set(accessEntries.map((access) => access.portfolioId).filter(Boolean))
      );

      const nextPortfolioMap: Record<string, string> = {};
      const nextCommunityMap: Record<string, string> = {};

      if (portfolio?.portfolioId) {
        nextPortfolioMap[portfolio.portfolioId] = portfolio.friendlyName;
      }

      communities.forEach((community) => {
        const name =
          community.friendlyName ||
          community.displayName ||
          community.address ||
          community.communityId;
        if (portfolio?.portfolioId) {
          nextCommunityMap[`${portfolio.portfolioId}:${community.communityId}`] = name;
        }
      });

      if (portfolioIds.length) {
        try {
          const response = await apiClient.get('/portfolios');
          (response.data || []).forEach((item: any) => {
            if (portfolioIds.includes(item.portfolioId)) {
              nextPortfolioMap[item.portfolioId] = item.friendlyName || item.portfolioId;
            }
          });
        } catch (error) {
          console.error('Failed to load portfolio names');
        }

        await Promise.all(
          portfolioIds.map(async (portfolioId) => {
            try {
              const response = await apiClient.get(
                `/portfolios/${portfolioId}/communities`
              );
              (response.data || []).forEach((community: any) => {
                const name =
                  community.friendlyName ||
                  community.displayName ||
                  community.address ||
                  community.communityId;
                nextCommunityMap[`${portfolioId}:${community.communityId}`] = name;
              });
            } catch (error) {
              console.error('Failed to load community names for portfolio', portfolioId);
            }
          })
        );
      }

      portfolioIds.forEach((portfolioId) => {
        if (!nextPortfolioMap[portfolioId]) {
          nextPortfolioMap[portfolioId] = portfolioId;
        }
      });

      setPortfolioNameMap(nextPortfolioMap);
      setCommunityNameMap(nextCommunityMap);
    };

    loadAccessNames();
  }, [tenantAccessMap, communities, portfolio]);

  React.useEffect(() => {
    const loadTenantAccessMap = async () => {
      if (!tenantView?.tenants?.length) {
        setTenantAccessMap({});
        return;
      }

      const results: Record<string, Array<{ portfolioId: string; communityId: string }>> = {};

      await Promise.all(
        tenantView.tenants.map(async (tenant) => {
          if (!tenant.emailNorm) {
            return;
          }
          try {
            const response = await apiClient.get(
              `/tenant-access/tenants/${encodeURIComponent(tenant.emailNorm)}/grants`
            );
            results[tenant.emailNorm] = response.data?.granted?.communityAccess || [];
            return;
          } catch (error: any) {
            if (error?.response?.status === 404) {
              results[tenant.emailNorm] = [];
            } else {
              results[tenant.emailNorm] = [];
            }
          }
        })
      );

      setTenantAccessMap(results);
    };

    loadTenantAccessMap();
  }, [tenantView]);

  const getAccessList = React.useCallback(
    (email: string) => tenantAccessMap[email] || [],
    [tenantAccessMap]
  );

  const getPortfolioName = React.useCallback(
    (accessPortfolioId: string) =>
      portfolioNameMap[accessPortfolioId] || accessPortfolioId,
    [portfolioNameMap]
  );

  const getCommunityName = React.useCallback(
    (accessPortfolioId: string, communityId: string) =>
      communityNameMap[`${accessPortfolioId}:${communityId}`] || communityId || 'Community',
    [communityNameMap]
  );

  const filteredTenants = React.useMemo(() => {
    if (!tenantView) {
      return [];
    }
    const term = tenantSearch.trim().toLowerCase();
    if (!term) {
      return tenantView.tenants;
    }

    return tenantView.tenants.filter((tenant) => {
      const name = `${tenant.firstName || ''} ${tenant.lastName || ''}`.trim().toLowerCase();
      return tenant.emailNorm.toLowerCase().includes(term) || name.includes(term);
    });
  }, [tenantView, tenantSearch]);

  return (
    <div className={styles.pageContainer}>
      <div className={styles.backLink}>
        <button onClick={() => navigate('/portfolios')}>← Back to Portfolios</button>
      </div>

      {portfolio && (
        <div className={styles.portfolioSummary}>
          <div className={styles.summaryHeader}>
            <h1>{portfolio.friendlyName}</h1>
            <div className={styles.summaryActions}>
              <DefaultButton
                text="Edit Portfolio"
                onClick={() => setIsEditPortfolioOpen(true)}
              />
            </div>
          </div>
          <div className={styles.summaryGrid}>
            <div>
              <span className={styles.label}>API Host:</span>
              <code className={styles.value}>{portfolio.apiHost}</code>
            </div>
            <div>
              <span className={styles.label}>Contact:</span>
              <span className={styles.value}>
                {portfolio.contact.name} ({portfolio.contact.email})
              </span>
            </div>
          </div>
        </div>
      )}

      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onDismiss={() => setNotification(null)}
        />
      )}

      <div className={styles.header}>
        <h2>Communities</h2>
        <div className={styles.headerActions}>
          <DefaultButton
            text="View Portfolio Tenants"
            onClick={loadPortfolioTenants}
            disabled={tenantLoading}
          />
          <DefaultButton
            text="Create Tenant"
            onClick={() => openCreateTenantModal()}
            disabled={!portfolioId}
          />
          <PrimaryButton
            iconProps={{ iconName: 'Add' }}
            text="Create Community"
            onClick={() => {
              setEditingCommunity(null);
              setIsCreateOpen(true);
            }}
          />
        </div>
      </div>

      {tenantView && (
        <div className={styles.tenantCard}>
          <div className={styles.tenantHeader}>
            <h3>{tenantView.title}</h3>
            <DefaultButton text="Close" onClick={() => setTenantView(null)} />
          </div>
          <div className={styles.searchRow}>
            <div className={styles.formGroup}>
              <label>Search</label>
              <input
                type="text"
                value={tenantSearch}
                onChange={(e) => setTenantSearch(e.target.value)}
                placeholder="Search by name or email"
              />
            </div>
          </div>
          <TenantDirectoryTable
            tenants={filteredTenants}
            totalCount={tenantView.tenants.length}
            loading={tenantLoading}
            filtersSummary={tenantSearch.trim() ? `Search=${tenantSearch.trim()}` : 'None'}
            onClearFilters={() => setTenantSearch('')}
            onRefresh={() => {
              if (tenantView.communityId) {
                loadCommunityTenants(tenantView.communityId);
              } else {
                loadPortfolioTenants();
              }
            }}
            emptyState={{
              isFiltered: Boolean(tenantSearch.trim()),
              emptyMessage: 'No tenants found.',
              filteredMessage: 'No results. Clear filters.',
            }}
            getAccessList={getAccessList}
            getPortfolioName={getPortfolioName}
            getCommunityName={getCommunityName}
            onManage={(email) =>
              navigate(`/tenants?manageEmail=${encodeURIComponent(email)}`)
            }
            onCopy={handleCopy}
            formatName={formatName}
          />
        </div>
      )}
      <StandardTable
        items={communities}
        totalCount={communities.length}
        loading={loading}
        filtersSummary="None"
        onClearFilters={() => undefined}
        onRefresh={loadData}
        emptyState={{
          isFiltered: false,
          emptyMessage: 'No communities yet. Create one to get started.',
          filteredMessage: 'No results. Clear filters.',
        }}
        renderTable={(paged) => (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Community Name</th>
                <th>Address</th>
                <th>Contact Email</th>
                <th>Contact Phone</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((community) => (
                <tr key={community.communityId}>
                  <td>
                    <strong>{community.friendlyName || community.displayName}</strong>
                  </td>
                  <td>{community.address}</td>
                  <td>{community.contact?.email || community.contactEmail || '—'}</td>
                  <td>{community.contact?.phone || community.contactPhone || '—'}</td>
                  <td>
                    <span className={`${styles.badge} ${styles.active}`}>
                      {community.status}
                    </span>
                  </td>
                  <td>
                    <div className={styles.rowActions}>
                      <DefaultButton
                        text="Edit"
                        onClick={() => handleEditCommunity(community)}
                        disabled={loading}
                      />
                      <DefaultButton
                        text="Tenants"
                        onClick={() => loadCommunityTenants(community.communityId)}
                        disabled={tenantLoading}
                      />
                      <DefaultButton
                        text="Create Tenant"
                        onClick={() => openCreateTenantModal(community.communityId)}
                        disabled={!portfolioId}
                      />
                      <DefaultButton
                        text="Delete"
                        onClick={() => {
                          const communityName =
                            community.friendlyName || community.displayName || community.address || community.communityId;
                          openConfirm({
                            title: 'Delete Community?',
                            body: (
                              <div>
                                <p>This will permanently delete {communityName}.</p>
                                <p>This action cannot be undone.</p>
                              </div>
                            ),
                            confirmLabel: 'Delete',
                            requireText: communityName,
                            requireTextLabel: `Type ${communityName} to confirm`,
                            onConfirm: () => handleDeleteCommunity(community.communityId),
                          });
                        }}
                        disabled={loading}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      />

      <CreateCommunityModal
        isOpen={isCreateOpen}
        portfolioId={portfolioId || ''}
        portfolioName={portfolio?.friendlyName}
        existingCommunity={editingCommunity || undefined}
        onDismiss={() => {
          setIsCreateOpen(false);
          setEditingCommunity(null);
        }}
        onSaved={(saved, isEdit) => {
          setNotification({
            type: 'success',
            message: isEdit ? 'Community updated successfully' : 'Community created successfully',
          });
          if (isEdit) {
            setCommunities((prev) =>
              prev.map((community) =>
                community.communityId === saved.communityId ? saved : community
              )
            );
          } else {
            setCommunities((prev) => [saved, ...prev]);
          }
        }}
        onError={(message) =>
          setNotification({
            type: 'error',
            message,
          })
        }
      />

      <CreatePortfolioModal
        isOpen={isEditPortfolioOpen}
        existingPortfolio={portfolio || undefined}
        onDismiss={() => setIsEditPortfolioOpen(false)}
        onSaved={(updated) => {
          setNotification({
            type: 'success',
            message: 'Portfolio updated successfully',
          });
          setPortfolio(updated);
        }}
        onError={(message) =>
          setNotification({
            type: 'error',
            message,
          })
        }
      />

      <CreateTenantModal
        isOpen={isCreateTenantOpen}
        portfolios={
          portfolio
            ? [{ portfolioId: portfolio.portfolioId, friendlyName: portfolio.friendlyName }]
            : []
        }
        communities={communities}
        loadCommunities={loadCommunitiesForModal}
        initialValues={createTenantDefaults}
        disablePortfolioSelect
        onDismiss={() => setIsCreateTenantOpen(false)}
        onCreated={(created) => {
          setNotification({ type: 'success', message: 'Tenant created' });

          localStorage.setItem('tenantDataVersion', Date.now().toString());
          window.dispatchEvent(new Event('tenant-data-updated'));

          if (tenantView) {
            const matchesPortfolio = created.portfolioId === tenantView.portfolioId;
            const matchesCommunity =
              !tenantView.communityId || created.communityId === tenantView.communityId;

            if (matchesPortfolio && matchesCommunity) {
              setTenantView((prev) => {
                if (!prev) {
                  return prev;
                }
                const exists = prev.tenants.some(
                  (tenant) => tenant.emailNorm === created.emailNorm
                );
                if (exists) {
                  return prev;
                }
                return {
                  ...prev,
                  tenants: [
                    {
                      emailNorm: created.emailNorm,
                      firstName: created.firstName,
                      lastName: created.lastName,
                    },
                    ...prev.tenants,
                  ],
                };
              });

              setTenantAccessMap((prev) => ({
                ...prev,
                [created.emailNorm]: [
                  { portfolioId: created.portfolioId, communityId: created.communityId },
                  ...(prev[created.emailNorm] || []).filter(
                    (access) =>
                      access.portfolioId !== created.portfolioId ||
                      access.communityId !== created.communityId
                  ),
                ],
              }));
            }
          }
        }}
        onViewTenant={(emailNorm) => {
          setIsCreateTenantOpen(false);
          navigate(`/tenants?manageEmail=${encodeURIComponent(emailNorm)}`);
        }}
        onNotify={(type, message) => setNotification({ type, message })}
      />
      {confirmDialog}
    </div>
  );
};
